import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18n';
import { useStepDetailRender, type OptionalString } from './hooks/useStepDetailRender';
import EditToolbar, { useEditToolbar } from '../EditToolbar';
import { useWarehousePermissionGuardedStartEdit } from '../../hooks/useWarehousePermissionGuardedStartEdit';
import { GET_TMS_TASK_DETAIL } from '../../graphql/queries/getTmsTaskDetail';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../../graphql/queries/getTmsTaskDetail';
import {
    HANDLE_TMS_WHS_UNSEALING_SCAN_EVENT_FOR_PORTAL,
    SUBMIT_TMS_WHS_UNSEALING_SCAN_EVENT_FOR_PORTAL,
    type HandleTmsWhsUnsealingScanEventInput,
} from '../../graphql/mutations/taskMutations';

interface UnsealingDetailProps {
    waypoint: TmsTaskWaypointType;
    stepDetailAllowEdit: boolean;
    task?: TmsTaskDetailType;
}

const UnsealingDetail: React.FC<UnsealingDetailProps> = ({ waypoint, stepDetailAllowEdit, task }) => {
    const formatTimeDisplay = (value: OptionalString) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : null);

    const [unSealTime, setUnSealTime] = useState<OptionalString>(formatTimeDisplay(waypoint.unSealTime));
    const [unSealTimeTimezone] = useState<OptionalString>(waypoint.unSealTimeTimezone);
    const [sealNumber, setSealNumber] = useState<string>(waypoint.unSealNumber || '');
    const [interiorPhotoUrls, setInteriorPhotoUrls] = useState<string[]>(waypoint.unSealInteriorPhotosUrlsJson || []);

    const [unSealTimeDraft, setUnSealTimeDraft] = useState<Dayjs | null>(
        waypoint.unSealTime ? dayjs(waypoint.unSealTime) : null
    );
    const [sealNumberDraft, setSealNumberDraft] = useState<string>(waypoint.unSealNumber || '');
    const [interiorPhotosDraft, setInteriorPhotosDraft] = useState<string[]>(waypoint.unSealInteriorPhotosJson || []);

    const { isEditing, startEdit, handleCancel, exitEdit } = useEditToolbar({
        onStartEdit: useCallback(() => {
            setUnSealTimeDraft(waypoint.unSealTime ? dayjs(waypoint.unSealTime) : null);
            setSealNumberDraft(waypoint.unSealNumber || '');
            setInteriorPhotosDraft(waypoint.unSealInteriorPhotosJson || []);
        }, [waypoint.unSealTime, waypoint.unSealNumber, waypoint.unSealInteriorPhotosJson]),
    });
    const guardedStartEdit = useWarehousePermissionGuardedStartEdit(waypoint.waypointId, startEdit);

    const [handleTmsWhsUnsealingScanEventMutation] = useMutation<
        { handleTmsWhsUnsealingScanEventForPortal: { success: boolean; message: string } },
        { input: HandleTmsWhsUnsealingScanEventInput }
    >(HANDLE_TMS_WHS_UNSEALING_SCAN_EVENT_FOR_PORTAL);

    const [submitTmsWhsUnsealingScanEventMutation] = useMutation<
        { submitTmsWhsUnsealingScanEventForPortal: { success: boolean; message: string } },
        { input: HandleTmsWhsUnsealingScanEventInput }
    >(SUBMIT_TMS_WHS_UNSEALING_SCAN_EVENT_FOR_PORTAL);

    const unSealTimeLabel = t('tms.task.drawer.unSealTime');
    const sealNumberLabel = t('tms.task.drawer.sealNumber');
    const interiorPhotoLabel = t('tms.task.drawer.unsealingInteriorPhotos');

    const { renderTimeRow, renderInputRow, renderPhotoRow } = useStepDetailRender({ isEditing });

    useEffect(() => {
        setUnSealTime(formatTimeDisplay(waypoint.unSealTime));
        setSealNumber(waypoint.unSealNumber || '');
        if (!isEditing) {
            setUnSealTimeDraft(waypoint.unSealTime ? dayjs(waypoint.unSealTime) : null);
            setSealNumberDraft(waypoint.unSealNumber || '');
        }
    }, [waypoint.unSealTime, waypoint.unSealNumber, isEditing]);

    useEffect(() => {
        setInteriorPhotoUrls(waypoint.unSealInteriorPhotosUrlsJson || []);
    }, [waypoint.unSealInteriorPhotosUrlsJson]);

    useEffect(() => {
        if (!isEditing) {
            setInteriorPhotosDraft(waypoint.unSealInteriorPhotosJson || []);
        }
    }, [waypoint.unSealInteriorPhotosJson, isEditing]);

    const validateSave = () => {
        if (!unSealTimeDraft) {
            message.error(t('tms.task.drawer.unSealTimeRequired'));
            return false;
        }
        const trimmed = sealNumberDraft.trim();
        if (!trimmed) {
            message.error(t('tms.task.drawer.sealNumberRequired'));
            return false;
        }
        if (trimmed.length > 32) {
            message.error(t('tms.task.drawer.sealNumberTooLong'));
            return false;
        }
        if (interiorPhotosDraft.length === 0) {
            message.error(t('tms.task.drawer.unsealingInteriorPhotosRequired'));
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateSave()) {
            return;
        }

        const unSealLocalTime = unSealTimeDraft ? unSealTimeDraft.format('YYYY-MM-DD HH:mm') : null;

        const input: HandleTmsWhsUnsealingScanEventInput = {
            taskId: waypoint.taskId,
            taskWayPointId: Number(waypoint.id),
            sealNumber: sealNumberDraft.trim(),
            unSealLocalTime,
            unSealInteriorPhotosJson: interiorPhotosDraft.length > 0 ? interiorPhotosDraft : null,
        };

        const refetchQueries = [{ query: GET_TMS_TASK_DETAIL, variables: { taskId: String(waypoint.taskId) } }];

        try {
            const { data: handleData } = await handleTmsWhsUnsealingScanEventMutation({
                variables: { input },
            });
            if (!handleData?.handleTmsWhsUnsealingScanEventForPortal.success) {
                throw new Error(
                    handleData?.handleTmsWhsUnsealingScanEventForPortal.message || t('tms.task.drawer.sealNumberSaveFailed')
                );
            }

            const { data: submitData } = await submitTmsWhsUnsealingScanEventMutation({
                variables: { input },
                refetchQueries,
            });
            if (!submitData?.submitTmsWhsUnsealingScanEventForPortal.success) {
                throw new Error(
                    submitData?.submitTmsWhsUnsealingScanEventForPortal.message || t('tms.task.drawer.sealNumberSaveFailed')
                );
            }

            setUnSealTime(formatTimeDisplay(unSealLocalTime));
            setSealNumber(sealNumberDraft.trim());
            message.success(t('tms.task.drawer.unSealNumberSaved'));
            exitEdit();
        } catch (error: any) {
            console.error('Failed to save unseal:', error);
            message.error(error?.message || t('tms.task.drawer.sealNumberSaveFailed'));
        }
    };

    return (
        <div className="relative border-gray-200">
            <EditToolbar
                isEditing={isEditing}
                allowEdit={stepDetailAllowEdit}
                onStartEdit={guardedStartEdit}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <div className="mt-1 space-y-3">
                {/* 解封时间 */}
                {renderTimeRow(
                    unSealTimeLabel,
                    unSealTime,
                    unSealTimeTimezone,
                    unSealTimeDraft,
                    setUnSealTimeDraft,
                    true,
                    waypoint.unSealTimeTimezone
                )}
                {/* 封签号（验证用） */}
                {renderInputRow(
                    sealNumberLabel,
                    sealNumber,
                    sealNumberDraft,
                    setSealNumberDraft,
                    t('tms.task.bol.placeholderSealNumber'),
                    32,
                    'max-w-[240px]',
                    true
                )}
                {/* 车内照片 */}
                {renderPhotoRow(
                    interiorPhotoLabel,
                    interiorPhotoUrls,
                    interiorPhotosDraft,
                    setInteriorPhotosDraft,
                    interiorPhotoUrls
                )}
            </div>
        </div>
    );
};

export default UnsealingDetail;
